import { messagesPath } from "@travily/routes";

export interface DockedChat {
  /** The counterpart's handle — the dock's identity, and what the visit stamp asks the server for. */
  handle: string;
  /** Held here rather than in the composer because the panel unmounts on /messages and below `lg`. */
  draft: string;
  minimised: boolean;
  /**
   * The newest message the reader has had on screen. Held here, not in the panel, because the panel
   * unmounts whenever the dock is not rendered — and a watermark that resets counts a whole history
   * as unread.
   */
  seenMessageId: number | null;
}

export interface DockedChatVisit {
  /** Where this visit is going — a dock never renders over the messages page, so it asks for nothing. */
  href: string;
  /** Speculative requests must not mark a thread read for a page the user may never open. */
  isPrefetch: boolean;
}

const DOCKED_CHAT_HEADER = "X-Docked-Chat";
const DOCKED_CHAT_FOCUSED_HEADER = "X-Docked-Chat-Focused";

let dockedChat: DockedChat | null = null;
// Whether the panel is on screen right now. Not part of DockedChat: it is the panel reporting a
// render, and folding it into the subscribed value would loop that render back into itself.
let isRendered = false;
const listeners = new Set<() => void>();

/**
 * The open docked chat. Module-scoped so it survives Inertia navigations and so the per-visit
 * header stamp — which runs outside React — can read it. The SSR bundle shares this module graph:
 * the server never has a dock open, and answering null there keeps one request's dock out of the
 * next request's render.
 */
export function getDockedChat(): DockedChat | null {
  if (typeof window === "undefined") return null;
  return dockedChat;
}

export function subscribeToDockedChat(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function openDockedChat(handle: string, draft = ""): void {
  setDockedChat({ handle, draft, minimised: false, seenMessageId: null });
}

export function closeDockedChat(): void {
  setDockedChat(null);
}

export function setDockedChatMinimised(minimised: boolean): void {
  const chat = getDockedChat();
  if (!chat || chat.minimised === minimised) return;
  setDockedChat({ ...chat, minimised });
}

export function setDockedChatDraft(draft: string): void {
  const chat = getDockedChat();
  if (!chat || chat.draft === draft) return;
  setDockedChat({ ...chat, draft });
}

export function setDockedChatSeenMessageId(seenMessageId: number | null): void {
  const chat = getDockedChat();
  if (!chat || chat.seenMessageId === seenMessageId) return;
  setDockedChat({ ...chat, seenMessageId });
}

/** The panel reporting whether it is on screen, which is what makes a read marker honest. */
export function setDockedChatRendered(rendered: boolean): void {
  isRendered = rendered;
}

// A visit's href may be absolute or relative and only its path is ever read, so the base exists
// solely to make `URL` accept the relative form.
const PATH_PARSE_BASE = "http://dock.invalid";

/** A dock never renders over the messages page — that page owns the thread. */
export function isMessagesRoute(href: string): boolean {
  const { pathname } = new URL(href, PATH_PARSE_BASE);
  return pathname === messagesPath() || pathname.startsWith(`${messagesPath()}/`);
}

/**
 * Stamped on every Inertia visit, which is what carries the docked conversation through a full
 * navigation instead of only a partial reload.
 *
 * The two headers answer different questions, so they are gated differently: the first asks the
 * server for the thread and rides any visit that could render the panel, while the second tells it
 * the thread is being read — and only a panel that is actually on screen, unminimised, in a visible
 * tab, on a request the user asked for, can say that truthfully.
 */
export function dockedChatHeaders({ href, isPrefetch }: DockedChatVisit): Record<string, string> {
  const chat = getDockedChat();
  if (!chat || isMessagesRoute(href)) return {};

  const isFocused = isRendered && !isPrefetch && !chat.minimised && document.visibilityState === "visible";
  return {
    [DOCKED_CHAT_HEADER]: chat.handle,
    ...(isFocused ? { [DOCKED_CHAT_FOCUSED_HEADER]: "true" } : {}),
  };
}

function setDockedChat(next: DockedChat | null): void {
  dockedChat = next;
  for (const listener of listeners) listener();
}
